import { quadBounds } from '@eip/engine';
import { mockupRunner } from '../app/session';
import { getPreviewMaps, getSceneCover, mockupPreviewKey, mockupPreviews, previews } from '../state/bitmaps';
import { previewMapsStale, sceneIdOf } from '../state/mockup-runner';
import { useStore } from '../state/store';
import type { GlPreviewState } from '../state/mockup';
import { sceneCoverKey } from './gl-job';
import { glPreviewLoseContext, glPreviewReadPixels, glPreviewRender } from './gl-preview';
import { comparedPixels, countedPixels, meanAbsError, meanAbsErrorWhere, offMask } from './mae';

/**
 * `window.__eip` — the app's only test seam, and deliberately a tiny one.
 *
 * The GPU preview's whole claim is that it draws the SAME image the worker's CPU compositor does, so
 * that a drag can show it and the commit can leave it up with no pop. Nothing inside the app can
 * check that: the two renders exist as an ImageBitmap and a WebGL drawing buffer, in a page, with a
 * real GPU underneath. So the check is exposed here and Playwright drives it against the production
 * build on headless Chromium.
 *
 * Installed unconditionally rather than behind a flag or an env check: it is three properties and a
 * function that does nothing until called, the production build would have to carry the branch
 * anyway, and a seam that only exists in a test build is a seam the test cannot trust.
 */
export interface EipHook {
  /** The renderer the UI believes it is on — the store's `glPreview`, which is what the inspector
   *  row and the banner read, rather than `gl-preview.ts`'s own view of it. */
  readonly renderer: GlPreviewState;
  /** Mean absolute error (0..1) between the GPU frame and the CPU render of the ACTIVE document's
   *  current job. Throws — with what was missing — rather than returning a number it cannot stand
   *  behind, so a test can never read a passing 0 from a comparison that did not happen. */
  mockupParity(): Promise<number>;
  /** The same comparison over the BACKDROP region instead — every pixel with `mask === 0`, which is
   *  where a backdrop swap does its work and where `mockupParity` deliberately looks at nothing.
   *  Throws on the same terms, and for the same reason. */
  mockupParityOutside(): Promise<number>;
  /** Kill the WebGL context on purpose, to watch the fallback by hand. False when there is no
   *  context or the browser does not offer `WEBGL_lose_context`. */
  loseContext(): boolean;
}

declare global {
  interface Window { __eip?: EipHook }
}

/** The two renders and the selections the measurements below are made over. */
interface Comparison {
  /** The GPU frame and the CPU render, both top-down RGBA at `width × height`. */
  gl: Uint8ClampedArray;
  cpu: Uint8ClampedArray;
  mask: Uint8Array;
  width: number;
  height: number;
  /** Every print area's bounding box test, for the on-print comparison. */
  inside: (x: number, y: number) => boolean;
  templateId: string;
  areas: number;
}

/**
 * The comparison both measurements are made from. It renders the GPU frame here and now — the
 * viewer may be showing one for a ghost placement, and this has to be the STORE's job, which is the
 * only one the CPU render can be compared against.
 */
async function compare(): Promise<Comparison> {
  const st = useStore.getState();
  const docId = st.activeId;
  if (!docId) throw new Error('mockup parity: no active document');
  const job = st.docs.find((d) => d.id === docId)?.mockup?.job;
  if (!job) throw new Error(`mockup parity: document ${docId} has no mockup job`);
  const template = st.templates.find((t) => t.template.id === job.templateId)?.template;
  if (!template) throw new Error(`mockup parity: template "${job.templateId}" is not in the registry`);
  const maps = getPreviewMaps(job.templateId);
  if (!maps) throw new Error(`mockup parity: no preview maps cached for template "${job.templateId}"`);
  // The SAME guard the viewer's `maps` memo applies. Without it this would happily compare a frame
  // drawn from a superseded preparation against the CPU render of the current one and blame the
  // renderer for the difference — the one failure a parity harness must not have.
  const prepared = st.templateStatus[job.templateId]?.prepared;
  if (previewMapsStale(maps, prepared)) {
    throw new Error(`mockup parity: the cached maps for "${job.templateId}" are from a superseded preparation (maps ${maps.prepGeneration}, template ${prepared?.prepGeneration ?? 'none'})`);
  }
  // A scene backdrop needs its cover on the GPU, and the harness asks for it rather than waiting to
  // be handed one: the viewer's own request is an effect that may not have run (or landed) yet, and
  // a comparison made without the cover would measure the GPU falling back to the photo's own
  // backdrop — a huge discrepancy that says nothing about the renderer. `ensureSceneCover` is
  // idempotent and shares the viewer's in-flight cut, so this costs nothing when it is already on
  // its way.
  const sceneId = sceneIdOf(job);
  let cover: ImageBitmap | undefined;
  let coverKey: string | null = null;
  if (sceneId) {
    coverKey = sceneCoverKey(sceneId, maps.width, maps.height);
    await mockupRunner.ensureSceneCover(sceneId, maps.width, maps.height);
    cover = getSceneCover(coverKey);
    if (!cover) throw new Error(`mockup parity: no cover cached for backdrop scene "${sceneId}" at ${maps.width}×${maps.height}`);
  }
  const own = previews.get(docId)?.output?.bitmap;
  if (!own) throw new Error(`mockup parity: document ${docId} has no DTF output preview to print`);
  // The same art resolution the viewer's GL path makes: this document's output is the primary print,
  // and an extra print's is its own document's (a `docId: null` print is this document's again).
  const arts: Record<string, ImageBitmap> = { [docId]: own };
  for (const p of job.extraPrints ?? []) {
    if (!p.enabled) continue;
    const id = p.docId ?? docId;
    const bitmap = previews.get(id)?.output?.bitmap;
    if (bitmap) arts[id] = bitmap;
  }
  if (!glPreviewRender(maps, template, job, arts, docId, coverKey && cover ? { [coverKey]: cover } : {})) {
    throw new Error('mockup parity: the GPU renderer produced no frame (no WebGL2, or the context is lost)');
  }
  const gl = glPreviewReadPixels();
  if (!gl) throw new Error('mockup parity: the GPU renderer had no frame to read back');
  const cpu = mockupPreviews.get(mockupPreviewKey(docId));
  if (!cpu) throw new Error(`mockup parity: no CPU mockup render cached for document ${docId}`);
  if (cpu.bitmap.width !== maps.width || cpu.bitmap.height !== maps.height) {
    throw new Error(`mockup parity: sizes differ — gl ${maps.width}×${maps.height}, cpu ${cpu.bitmap.width}×${cpu.bitmap.height}`);
  }
  // The CPU render is an ImageBitmap, which has no readable pixels: a scratch 2D canvas is the only
  // way to them. `willReadFrequently` keeps the one `getImageData` off the GPU-backed path, where a
  // readback of a 1536² canvas is the slow part of this whole function.
  const scratch = document.createElement('canvas');
  scratch.width = maps.width;
  scratch.height = maps.height;
  const ctx = scratch.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('mockup parity: could not get a 2d context for the scratch canvas');
  ctx.drawImage(cpu.bitmap, 0, 0);
  const cpuPixels = ctx.getImageData(0, 0, maps.width, maps.height).data;
  // Every print area's bounding box, in PREVIEW pixels (`maps.areas` are already at this scale).
  // The union of the boxes rather than the quads themselves: a box is cheap to test, and a pixel
  // inside the box but outside a keystoned quad is one both renderers leave as bare garment anyway.
  const boxes = maps.areas.map((a) => quadBounds(a.quad));
  const inside = (x: number, y: number): boolean => boxes.some((b) => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1);
  return { gl, cpu: cpuPixels, mask: maps.mask, width: maps.width, height: maps.height, inside, templateId: job.templateId, areas: maps.areas.length };
}

/** §14 parity: the print. The pixels on the garment and inside a print area — where the ink is. */
async function mockupParity(): Promise<number> {
  const c = await compare();
  // `meanAbsError` returns 0 when it counts nothing, and hands the caller the decision about what
  // that means (see its doc comment). Here it means the comparison did not happen — an empty mask, a
  // degenerate area — and handing that 0 back would be the false pass this whole function exists to
  // make impossible.
  const n = comparedPixels(c.mask, c.inside, c.width, c.height);
  if (n === 0) throw new Error(`mockup parity: no pixels to compare — nothing in the ${c.areas} print area(s) of "${c.templateId}" is on the garment mask`);
  return meanAbsError(c.gl, c.cpu, c.mask, c.inside, c.width, c.height);
}

/**
 * §14.3 parity: the BACKDROP. The pixels with no garment at all, which is the whole region a
 * backdrop swap repaints — and the region `mockupParity` is blind to by construction, so without
 * this one the GPU could draw the wrong backdrop entirely and still report 1e-6.
 *
 * The same empty-count refusal: a photo whose every pixel is garment has nothing to compare, and
 * handing back the 0 that produces would be the false pass again.
 */
async function mockupParityOutside(): Promise<number> {
  const c = await compare();
  const keep = offMask(c.mask, c.width);
  const n = countedPixels(keep, c.width, c.height);
  if (n === 0) throw new Error(`mockup parity: no pixels to compare — every pixel of "${c.templateId}" is on the garment mask`);
  return meanAbsErrorWhere(c.gl, c.cpu, keep, c.width, c.height);
}

let installed = false;

/** Publish `window.__eip`, once. Called from `useMockupView`, which mounts with the viewer. */
export function installEipHook(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.__eip = {
    // A getter, not a snapshot: the hook writes `glPreview` as frames land, and a test reading
    // `__eip.renderer` wants the current answer rather than the one from page load.
    get renderer(): GlPreviewState { return useStore.getState().glPreview; },
    mockupParity,
    mockupParityOutside,
    loseContext: glPreviewLoseContext,
  };
}
